import { CreditCard } from 'lucide-react';
import { BaseNode } from './BaseNode';
import { StripeConfig } from './StripeConfig';
import type { CustomNodeProps, DataSchema } from '../../types/nodes';
import type { IntegrationsNodeData } from '../../types/nodes';

// Define comprehensive output schema for Stripe operations
const stripeOutputSchema: Record<string, DataSchema> = {
	result: {
		type: 'object',
		description: 'Stripe operation result',
		properties: {
			// Common Stripe object properties
			id: {
				type: 'string',
				description: 'Stripe object ID (e.g., payment_intent_id, customer_id)',
			},
			object: {
				type: 'string',
				description: 'Stripe object type (e.g., payment_intent, customer, subscription)',
			},
			created: {
				type: 'number',
				description: 'Unix timestamp when the object was created',
			},
			livemode: {
				type: 'boolean',
				description: 'Whether this object was created in live mode',
			},
			
			// Payment Intent specific outputs
			amount: {
				type: 'number',
				description: 'Amount in cents/smallest currency unit',
			},
			currency: {
				type: 'string',
				description: 'Three-letter ISO currency code',
			},
			status: {
				type: 'string',
				description: 'Current status of the payment intent',
			},
			client_secret: {
				type: 'string',
				description: 'Client secret for frontend confirmation',
			},
			
			// Customer specific outputs
			email: {
				type: 'string',
				description: 'Customer email address',
			},
			name: {
				type: 'string',
				description: 'Customer name',
			},
			description: {
				type: 'string',
				description: 'Customer description',
			},
			
			// Subscription specific outputs
			subscription_id: {
				type: 'string',
				description: 'Stripe subscription ID',
			},
			price_id: {
				type: 'string',
				description: 'Stripe price ID',
			},
			current_period_start: {
				type: 'number',
				description: 'Start of current billing period',
			},
			current_period_end: {
				type: 'number',
				description: 'End of current billing period',
			},
			
			// Invoice specific outputs
			invoice_id: {
				type: 'string',
				description: 'Stripe invoice ID',
			},
			invoice_number: {
				type: 'string',
				description: 'Human-readable invoice number',
			},
			total: {
				type: 'number',
				description: 'Total amount for the invoice',
			},
			
			// Refund specific outputs
			refund_id: {
				type: 'string',
				description: 'Stripe refund ID',
			},
			refund_amount: {
				type: 'number',
				description: 'Amount refunded',
			},
			refund_reason: {
				type: 'string',
				description: 'Reason for the refund',
			},
			
			// Payment Method specific outputs
			payment_method_id: {
				type: 'string',
				description: 'Stripe payment method ID',
			},
			card_brand: {
				type: 'string',
				description: 'Card brand (visa, mastercard, etc.)',
			},
			card_last4: {
				type: 'string',
				description: 'Last 4 digits of the card',
			},
			card_exp_month: {
				type: 'number',
				description: 'Card expiration month',
			},
			card_exp_year: {
				type: 'number',
				description: 'Card expiration year',
			},
		},
	},
	
	// Metadata about the operation
	metadata: {
		type: 'object',
		description: 'Operation metadata and context',
		properties: {
			nodeType: {
				type: 'string',
				description: 'Type of node that executed',
			},
			operation: {
				type: 'string',
				description: 'Stripe operation performed',
			},
			stripeObjectId: {
				type: 'string',
				description: 'ID of the created/updated Stripe object',
			},
			amount: {
				type: 'number',
				description: 'Amount involved in the operation',
			},
			currency: {
				type: 'string',
				description: 'Currency used for the operation',
			},
			executionTime: {
				type: 'number',
				description: 'Execution time in milliseconds',
			},
		},
	},
	
	// Success status
	success: {
		type: 'boolean',
		description: 'Whether the operation was successful',
	},
};

export function StripeNode(props: CustomNodeProps) {
	// Ensure the node has the correct category for proper handling
	const nodeData: IntegrationsNodeData = {
		...props.data,
		category: 'integrations',
		type: 'stripe', // This is the correct type for IntegrationsNodeData
		config: props.data.config || {},
	};

	return (
		<BaseNode
			{...props}
			data={nodeData}
			icon={CreditCard}
			configComponent={StripeConfig as any}
		/>
	);
}
